import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Form } from 'formik-antd';
import { Formik } from 'formik';
import { Button, Modal, Tooltip } from 'antd';
import cx from 'classnames';
import * as Yup from 'yup';

import FormItem from 'components/formik/FormItem';
import { BaseModal, BaseModalFooter } from 'components/wrappers/BaseModal';
import FormikSelectWithLoading from 'hocs/withLoading/FormikSelectWithLoading';
import { useAppDispatch } from 'hooks/useAppDispatch';
import BaseAlert from 'components/wrappers/BaseAlert';
import {
  getAssignmentPayCodeGroups,
  getAssignmentTemplateNames,
  getEditInfoForAssignmentTemplate,
} from 'redux/reducers/assignmentSlice';
import { ApiErrorMessage } from 'constants/ErrorMessages';
import SubmitButton from 'components/formik/SubmitButton';
import useModalCloseOnNavigate from 'hooks/useModalCloseOnNavigate';
import {
  isAdminSelector,
  isCorporateTASelector,
  isRecruiterCompanySelector,
  userSelector,
} from 'redux/selectors/authenticationSelectors';
import { useAppSelector } from 'hooks/useAppSelector';
import AssignmentModal from '../AssignmentDataModal/AssignmentModal';
import {
  AssignmentTemplateMiniViewModel,
  AssignmentTemplateResponseModel,
  BOUsersPriceAgreementRespModel,
  CustomerResponseModel,
} from 'api/backofficeApiClient';
import { mapAssigmentModelToInitialValues } from 'features/assignment/AssignmentDetailsPage/utils';
import { AssignmentCreationTypeEnum } from '../AssignmentDataModal/enum';
import { getMemberPriceAgreement } from 'redux/reducers/recruiterSlice';
import { getPricingAgreement } from 'redux/reducers/accountSlice';
import {
  getApprovedClientsByMember,
  getMyApprovedClients,
} from 'redux/reducers/clientSlice';
import {
  payCodesDetailsInfoSelector,
  payCodesSelector,
  payCodesGroupsSelector,
} from 'redux/selectors/assignmentSelectors';

type Props = {
  onClose: () => void;
};

interface FormValues {
  template: string;
}

const validationSchema = Yup.object({
  template: Yup.string(),
});

export default function TemplateAssignmentModal(props: Props) {
  const { onClose } = props;
  const [templates, setTemplates] = useState<AssignmentTemplateMiniViewModel[]>(
    [],
  );
  const [isTemplatesLoading, setIsTemplatesLoading] = useState(false);
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
  const [isAssignmentModalOpen, setIsAssignmentModalOpen] = useState(false);
  const [
    isSelectedTemplateDetailsLoading,
    setIsSelectedTemplateDetailsLoading,
  ] = useState(false);
  const [selectedTemplate, setSelectedTemplate] =
    useState<AssignmentTemplateResponseModel>();
  const [priceAgreement, setPriceAgreement] =
    useState<BOUsersPriceAgreementRespModel>();
  const [customers, setCustomers] = useState<CustomerResponseModel[]>([]);
  const [isCustomersLoaded, setIsCustomersLoaded] = useState(false);
  const isCorporateTA = useAppSelector(isCorporateTASelector);
  const isRecruiterCompany = useAppSelector(isRecruiterCompanySelector);
  const user = useAppSelector(userSelector);
  const payCodesDetails = useAppSelector(payCodesSelector);
  const payCodesDetailsInfo = useAppSelector(payCodesDetailsInfoSelector);
  const payCodesGroups = useAppSelector(payCodesGroupsSelector);
  const dispatch = useAppDispatch();
  const isAdminRole = useAppSelector(isAdminSelector);

  const formRef: any = useRef();

  useModalCloseOnNavigate(onClose);

  const initialValues: FormValues = {
    template: '',
  };

  const handleModalCancel = () => {
    if (formRef.current.isSubmitting) return;

    if (formRef.current?.dirty) {
      return Modal.confirm({
        title: 'Are you sure? The data will not be saved.',
        icon: <span className="icon icon-attention-filled" />,
        onOk() {
          onClose();
        },
        onCancel() {},
      });
    }
    onClose();
  };

  const loadTemplateNames = useCallback(async () => {
    setIsTemplatesLoading(true);
    const result = await dispatch(getAssignmentTemplateNames());

    if (getAssignmentTemplateNames.fulfilled.match(result)) {
      setTemplates(result.payload);

      if (result.payload.length) {
        setIsTemplateModalOpen(true);
      } else {
        setIsAssignmentModalOpen(true);
      }
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
    setIsTemplatesLoading(false);
  }, [dispatch]);

  const loadPriceAgreemnt = async () => {
    const correctAction: any = isAdminRole
      ? getMemberPriceAgreement
      : getPricingAgreement;

    const result = await dispatch(
      correctAction(isAdminRole ? user?.companyId : undefined),
    );

    if (correctAction.fulfilled.match(result)) {
      setPriceAgreement(result.payload);
    }
  };

  const loadCustomers = async () => {
    if (isCorporateTA) return;

    const correctAction: any = isAdminRole
      ? getApprovedClientsByMember
      : getMyApprovedClients;

    const result = await dispatch(
      correctAction(isAdminRole ? user?.companyId : undefined),
    );

    if (correctAction.fulfilled.match(result)) {
      setCustomers(result.payload);
    }
    setIsCustomersLoaded(true);
  };

  useEffect(() => {
    loadTemplateNames();
    loadCustomers();
    loadPriceAgreemnt();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    dispatch(getAssignmentPayCodeGroups(isAdminRole));
  }, [dispatch, isAdminRole]);

  const handleSubmit = async (data: FormValues) => {
    if (data.template) {
      setIsSelectedTemplateDetailsLoading(true);

      const result = await dispatch(
        getEditInfoForAssignmentTemplate({ assignmentId: data.template }),
      );

      if (getEditInfoForAssignmentTemplate.fulfilled.match(result)) {
        setSelectedTemplate(result.payload);
      } else {
        BaseAlert.error(result.error.message || ApiErrorMessage);
      }

      setIsSelectedTemplateDetailsLoading(false);
    }
    setIsTemplateModalOpen(false);
    setIsAssignmentModalOpen(true);
  };

  return (
    <>
      {isAssignmentModalOpen && (
        <AssignmentModal
          onClose={onClose}
          companyId={user?.companyId}
          isCorporateTA={!!isCorporateTA}
          isRecruiter={!!isRecruiterCompany}
          type={selectedTemplate?.assignmentType}
          initialValues={
            selectedTemplate &&
            mapAssigmentModelToInitialValues({
              editAssignmentInfo: selectedTemplate,
              payCodesDetails,
              payCodesDetailsInfo,
              payCodesGroups,
            })
          }
          assignmentCreationType={
            selectedTemplate
              ? AssignmentCreationTypeEnum.Template
              : AssignmentCreationTypeEnum.Create
          }
          workerFileInfo={
            selectedTemplate?.assignmentDocument?.workerInfo?.workerFile
          }
          contractorCompanyFileInfo={
            selectedTemplate?.assignmentDocument?.contractorCompanyInfo
              ?.workerFile
          }
          priceAgreement={priceAgreement}
          customers={customers}
          isCustomersLoaded={isCustomersLoaded}
        />
      )}
      {isTemplateModalOpen && (
        <BaseModal
          title="Select a template"
          open
          closable={true}
          destroyOnClose
          maxWidth={468}
          onCancel={handleModalCancel}
        >
          <Formik
            innerRef={formRef}
            initialValues={initialValues}
            onSubmit={handleSubmit}
            validationSchema={validationSchema}
          >
            <Form layout="vertical">
              <div className="modal-scroll-wrapper">
                <div className="modal-capture">
                  Do you want to use one of your existing templates for this
                  assignment?
                </div>

                <FormItem
                  label={
                    <>
                      <span>Template </span>
                      <Tooltip
                        title="Select an existing template to pre-fill the fields, which you will be able to edit."
                        trigger="click"
                        placement="topLeft"
                      >
                        <span
                          className={cx(
                            'icon icon-attention-filled modal-tooltip-opener',
                          )}
                        />
                      </Tooltip>
                    </>
                  }
                  name="template"
                >
                  <FormikSelectWithLoading
                    isLoading={isTemplatesLoading}
                    name="template"
                    placeholder="No template selected"
                  >
                    {templates.map((template) => (
                      <FormikSelectWithLoading.Option
                        key={`select-template-${template.id}`}
                        value={template.id as string}
                      >
                        {template.templateName}
                      </FormikSelectWithLoading.Option>
                    ))}
                  </FormikSelectWithLoading>
                </FormItem>
              </div>

              <BaseModalFooter>
                <div>
                  <Button
                    onClick={handleModalCancel}
                    data-id="cancelBtn"
                    className="d-none"
                  >
                    Cancel
                  </Button>

                  <SubmitButton
                    type="primary"
                    loading={isSelectedTemplateDetailsLoading}
                    data-id="createNewAssignmentBtn"
                  >
                    Next
                  </SubmitButton>
                </div>
              </BaseModalFooter>
            </Form>
          </Formik>
        </BaseModal>
      )}
    </>
  );
}
